import React, { useEffect, useState, useRef } from 'react';
import { XIcon, PencilIcon, ChevronLeftIcon, ChevronRightIcon, ShoppingBagIcon } from 'lucide-react';
import { categoryLabels, stockColors, stockLabels } from './constants';
import { useRoleTheme } from '../../../hooks/useRoleTheme';
interface ItemDisplayProps {
  item: any;
  darkMode: boolean;
  textPrimary: string;
  textSecondary: string;
  onClose: () => void;
  onEdit: (id: string) => void;
}
export const ItemDisplay: React.FC<ItemDisplayProps> = ({
  item,
  darkMode,
  textPrimary,
  textSecondary,
  onClose,
  onEdit
}) => {
  const rt = useRoleTheme();
  return <div className="h-full flex flex-col relative" data-id="element-20041" data-mp="2dmk5r4zef3hg">
      <div className="flex-1 overflow-y-auto" data-id="element-20042" data-mp="2dpg06jc1fa15">
        <div className="flex justify-center pt-3 pb-1 md:hidden" data-id="element-20043" data-mp="28nh13lnbyza0">
          <div className={`w-10 h-1 rounded-full ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} data-id="element-20044" data-mp="1286myeji5e8a" />
        </div>
        <button onClick={onClose} className={`absolute top-3 right-3 p-2 rounded-full ${darkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'} transition-colors`} data-id="element-20045" data-mp="x8bx8ebgquot">
          <XIcon className="h-4 w-4" data-id="element-20046" data-mp="2eh41n2igny3f" />
        </button>
        <div className="px-5 pb-6 pt-3 md:pt-5" data-id="element-20047" data-mp="20ivy2bqx5yja">
          <div className="flex gap-4 items-start" data-id="element-20048" data-mp="25is40gj6g01h">
            <div className="flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden shadow-lg" data-id="element-20049" data-mp="25hgyoca32k89">
              <img src={item.coverImage} alt={item.name} className="w-full h-full object-cover" data-id="element-20050" data-mp="1qst52bjsskky" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-20051" data-mp="1htplz7kms56g">
              <h3 className={`font-bold text-base leading-tight line-clamp-2 mb-1 ${textPrimary}`} data-id="element-20052" data-mp="1hv0s3e1jtp0q">
                {item.name}
              </h3>
              <div className="flex flex-wrap gap-1 mb-2" data-id="element-20053" data-mp="17wb8ngu07wid">
                <span className={`text-xs px-2 py-0.5 ${rt.badgeRadius} font-medium ${darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'}`} data-id="element-20054" data-mp="1cttkbezeopu2">
                  {categoryLabels[item.category] || item.category}
                </span>
                <span className={`text-xs px-2 py-0.5 ${rt.badgeRadius} font-medium text-white ${stockColors[item.stockStatus] || 'bg-gray-500'}`} data-id="element-20055" data-mp="owwvz5a9aihy">
                  {stockLabels[item.stockStatus] || item.stockStatus}
                </span>
              </div>
              <span className={`text-lg font-bold ${textPrimary}`} data-id="element-20056" data-mp="1clzppbi601st">
                ${item.price.toFixed(2)}
              </span>
            </div>
          </div>
          {item.description && <p className={`mt-3 text-sm ${textSecondary} line-clamp-2`} data-id="element-20057" data-mp="12rzc0l8tho8h">
              {item.description}
            </p>}
          <div className="mt-4 flex gap-2" data-id="element-20058" data-mp="3ngwtpj09z48s">
            <button onClick={() => {
            onEdit(item.id);
            onClose();
          }} className="flex-1 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-semibold text-sm flex items-center justify-center gap-2 transition-colors" data-id="element-20059" data-mp="3nhopouxsb3zl">
              <PencilIcon className="h-4 w-4" data-id="element-20060" data-mp="22w9rtb176zsz" />
              Edit Product
            </button>
            <button onClick={onClose} className={`px-4 py-2.5 rounded-xl text-sm font-medium border transition-colors ${darkMode ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-id="element-20061" data-mp="27vcqmdql49si">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>;
};
export const MerchHoverCard: React.FC<{
  item: any;
  darkMode: boolean;
  mousePosition: {
    x: number;
    y: number;
  };
}> = ({
  item,
  darkMode,
  mousePosition
}) => {
  if (!item) return null;
  const showOnLeft = mousePosition.x > window.innerWidth / 2;
  const cardWidth = 240;
  return <div className={`fixed z-30 w-60 p-4 rounded-lg shadow-md border ${darkMode ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-300'}`} style={{
    top: Math.max(mousePosition.y - 70, 20),
    left: showOnLeft ? Math.max(mousePosition.x - cardWidth - 20, 20) : Math.min(mousePosition.x + 20, window.innerWidth - cardWidth - 20)
  }} data-id="element-20062" data-mp="11a207s3qbnxy">
      <div className="flex gap-3" data-id="element-20063" data-mp="2wpp9fw67es9l">
        <div className="w-14 h-14 flex-shrink-0 rounded bg-center bg-cover" style={{
        backgroundImage: `url(${item.coverImage})`
      }} data-id="element-20064" data-mp="28zmm1td8iahb" />
        <div className="flex-1 overflow-hidden" data-id="element-20065" data-mp="23yfftyqqpjcr">
          <h3 className="font-bold text-sm mb-1 line-clamp-2" data-id="element-20066" data-mp="2hsyk6fq8uwxp">{item.name}</h3>
          <p className={`text-xs mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-20067" data-mp="2d1gfbgl3oc7g">
            {categoryLabels[item.category] || item.category}
          </p>
          <div className="flex items-center gap-2" data-id="element-20068" data-mp="1la0m33p1fr89">
            <span className="text-sm font-bold" data-id="element-20069" data-mp="1l9iu4j9erz65">${item.price.toFixed(2)}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded-full text-white ${stockColors[item.stockStatus] || 'bg-gray-500'}`} data-id="element-20070" data-mp="1b52fft7o0o3x">
              {stockLabels[item.stockStatus] || item.stockStatus}
            </span>
          </div>
        </div>
      </div>
    </div>;
};
export const CoverCarousel: React.FC<{
  items: any[];
  darkMode: boolean;
  onItemClick: (item: any) => void;
  onHoverEnter: (id: string) => void;
  onHoverLeave: () => void;
}> = ({
  items,
  darkMode,
  onItemClick,
  onHoverEnter,
  onHoverLeave
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const checkScroll = () => {
    if (scrollRef.current) {
      const {
        scrollLeft,
        scrollWidth,
        clientWidth
      } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };
  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) el.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    return () => {
      if (el) el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [items]);
  const scroll = (dir: 'left' | 'right') => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: dir === 'left' ? -scrollRef.current.clientWidth * 0.75 : scrollRef.current.clientWidth * 0.75,
        behavior: 'smooth'
      });
      setTimeout(checkScroll, 350);
    }
  };
  return <div className="relative group" data-id="element-20071" data-mp="1nifk38g2r6e8">
      <div ref={scrollRef} className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide" style={{
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    }} onScroll={checkScroll} data-id="element-20072" data-mp="1ihrvetfbj1n6">
        {items.map((item: any) => <div key={item.id} className="flex-shrink-0 w-32 sm:w-36 cursor-pointer group/item" onClick={() => onItemClick(item)} onMouseEnter={() => onHoverEnter(item.id)} onMouseLeave={onHoverLeave} data-id="element-20073" data-mp="3ctgaqwpgp683">
            <div className="w-full aspect-square bg-center bg-cover rounded-lg shadow-md transition-transform duration-200 ease-in-out group-hover/item:scale-[1.04] group-hover/item:shadow-xl" style={{
          backgroundImage: `url('${item.coverImage}')`
        }} title={item.name} data-id="element-20074" data-mp="3u5divq8idiyi" />
          </div>)}
      </div>
      {canScrollLeft && <button onClick={() => scroll('left')} className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 p-2 rounded-full shadow-md ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'}`} data-id="element-20075" data-mp="3bggntk5aemf8">
          <ChevronLeftIcon className="h-4 w-4" data-id="element-20076" data-mp="vostlwhnr804" />
        </button>}
      {canScrollRight && <button onClick={() => scroll('right')} className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 p-2 rounded-full shadow-md ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'}`} data-id="element-20077" data-mp="1gonz8tbf2sx6">
          <ChevronRightIcon className="h-4 w-4" data-id="element-20078" data-mp="1zhhzsi8amihv" />
        </button>}
    </div>;
};
export const ItemGrid: React.FC<{
  items: any[];
  darkMode: boolean;
  onItemClick: (item: any) => void;
  onHoverEnter: (id: string) => void;
  onHoverLeave: () => void;
}> = ({
  items,
  darkMode,
  onItemClick,
  onHoverEnter,
  onHoverLeave
}) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  return <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-id="element-20079" data-mp="f14ysw5nbj46">
      {items.map((item: any) => <div key={item.id} className="cursor-pointer group" onClick={() => onItemClick(item)} onMouseEnter={() => !isMobile && onHoverEnter(item.id)} onMouseLeave={onHoverLeave} data-id="element-20080" data-mp="a3ljdsx7p0an">
          <div className="w-full aspect-square bg-center bg-cover rounded-lg shadow-md transition-transform duration-200 ease-in-out group-hover:scale-[1.03] group-hover:shadow-xl" style={{
        backgroundImage: `url('${item.coverImage}')`
      }} title={item.name} data-id="element-20081" data-mp="2qr3508cf4etm" />
        </div>)}
    </div>;
};
export const MerchInfoModal = ItemDisplay;